import { useMemo } from 'react';
import { useLeads, useReports, useStages } from '../api/queries';
import { fmtDate, fmtILS, daysSince, isOverdue, STALE_DAYS } from '../utils/format';

export function OverviewView({ onSelectLead }: { onSelectLead: (id: string) => void }) {
  const { data: stagesData } = useStages();
  const { data: leadsData } = useLeads();
  const { data: reports } = useReports();

  const stages = useMemo(
    () => (stagesData?.items ?? []).slice().sort((a, b) => a.order - b.order),
    [stagesData],
  );
  const leads = leadsData?.items ?? [];

  const wonId = stages.find((s) => s.isWon)?.id;
  const lostId = stages.find((s) => s.isLost)?.id;
  const activeLeads = leads.filter((l) => l.stageId !== wonId && l.stageId !== lostId);
  const wonLeads = leads.filter((l) => l.stageId === wonId);

  const sumNre = (xs: typeof leads) => xs.reduce((s, l) => s + (Number(l.nreValue) || 0), 0);
  const sumYearly = (xs: typeof leads) =>
    xs.reduce((s, l) => s + (Number(l.dealValueYearly) || 0), 0);

  const upcoming = useMemo(
    () =>
      leads
        .filter((l) => l.nextActionDate && l.stageId !== wonId && l.stageId !== lostId)
        .slice()
        .sort(
          (a, b) => new Date(a.nextActionDate!).getTime() - new Date(b.nextActionDate!).getTime(),
        )
        .slice(0, 15),
    [leads, wonId, lostId],
  );

  const staleLeads = activeLeads.filter((l) => daysSince(l.lastActivityAt) >= STALE_DAYS);

  const forecastYearly = reports?.totals.forecastYearly ?? 0;
  const goals = reports?.goals;

  return (
    <div className="p-3 md:p-6 overflow-y-auto overscroll-contain h-full">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-4 mb-4 md:mb-6">
        <Card label="Total Leads" value={leads.length}>
          <span>
            {activeLeads.length} active · {wonLeads.length} won ·{' '}
            {leads.length - activeLeads.length - wonLeads.length} lost
          </span>
        </Card>
        <Card
          label="Weighted forecast (yearly)"
          value={fmtILS(forecastYearly)}
          valueClass="text-accent2"
        >
          <span>vs raw pipeline {fmtILS(sumYearly(activeLeads))}/yr</span>
        </Card>
        <Card
          label="Won this quarter"
          value={goals?.actualDeals ?? wonLeads.length}
          valueClass="text-green"
        >
          <span>{fmtILS(goals?.actualRevenue ?? sumYearly(wonLeads))}/yr closed</span>
        </Card>
        <Card
          label="Stale leads"
          value={staleLeads.length}
          valueClass={staleLeads.length > 0 ? 'text-red' : 'text-green'}
        >
          <span>{STALE_DAYS}+ days no activity</span>
        </Card>
      </div>

      {goals && (goals.goalDeals != null || goals.goalRevenue != null) && (
        <div className="bg-surface border border-border rounded-xl p-4 md:p-5 mb-4 md:mb-6">
          <h3 className="text-sm font-semibold text-text2 mb-3">Quarterly goal progress</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {goals.goalDeals != null && (
              <ProgressBar label="Deals won" actual={goals.actualDeals} goal={goals.goalDeals} />
            )}
            {goals.goalRevenue != null && (
              <ProgressBar
                label="Revenue (yearly)"
                actual={goals.actualRevenue}
                goal={goals.goalRevenue}
                fmt={fmtILS}
              />
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <div className="bg-surface border border-border rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-text2 mb-4">Pipeline by Stage</h3>
          <div className="flex flex-col gap-2.5">
            {stages.map((stage) => {
              const sLeads = leads.filter((l) => l.stageId === stage.id);
              const sNre = sumNre(sLeads);
              const sYr = sumYearly(sLeads);
              const pct = leads.length ? (sLeads.length / leads.length) * 100 : 0;
              return (
                <div
                  key={stage.id}
                  className="grid grid-cols-[1fr_auto] sm:grid-cols-[140px_1fr_160px] gap-x-2.5 gap-y-1 items-center"
                >
                  <div className="flex items-center gap-1.5 text-sm min-w-0">
                    <span
                      className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                      style={{ background: stage.color }}
                    />
                    <span className="truncate">{stage.name}</span>
                    <span className="text-[11px] text-text3 bg-surface2 px-1.5 rounded-md flex-shrink-0">
                      {sLeads.length}
                    </span>
                  </div>
                  <div className="h-1.5 bg-surface2 rounded overflow-hidden col-span-2 sm:col-span-1 order-last sm:order-none">
                    <div
                      className="h-full rounded transition-[width] duration-300"
                      style={{ width: `${pct}%`, background: stage.color }}
                    />
                  </div>
                  <div className="flex gap-2 text-[11px] text-text2 justify-end whitespace-nowrap">
                    {sNre > 0 && <span>NRE {fmtILS(sNre)}</span>}
                    {sYr > 0 && <span className="text-green">{fmtILS(sYr)}/yr</span>}
                    {sNre === 0 && sYr === 0 && <span className="text-text3">—</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-text2 mb-4">Upcoming Actions</h3>
          {upcoming.length === 0 && (
            <div className="text-text3 text-sm p-2">No upcoming actions scheduled</div>
          )}
          <div className="flex flex-col gap-1.5 md:max-h-[340px] md:overflow-y-auto">
            {upcoming.map((lead) => {
              const stage = stages.find((s) => s.id === lead.stageId);
              const overdue = isOverdue(lead.nextActionDate);
              return (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => onSelectLead(lead.id)}
                  className="grid grid-cols-[64px_1fr_auto] sm:grid-cols-[80px_120px_1fr_auto] gap-x-2.5 gap-y-0.5 items-center px-2.5 py-2.5 sm:py-2 bg-surface2 hover:bg-surface3 active:bg-surface3 rounded-md cursor-pointer text-sm text-left w-full"
                >
                  <div className={overdue ? 'text-red text-xs' : 'text-text2 text-xs'}>
                    {overdue && '⏰ '}
                    {fmtDate(lead.nextActionDate)}
                  </div>
                  <div className="font-semibold truncate">{lead.company}</div>
                  <div className="text-text2 truncate col-start-2 sm:col-start-auto text-xs sm:text-sm">
                    {lead.nextAction || '—'}
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] text-text3 row-start-1 col-start-3 sm:row-start-auto sm:col-start-auto">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: stage?.color ?? '#666' }}
                    />
                    <span className="truncate max-w-[90px] sm:max-w-none">{stage?.name}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({
  label,
  value,
  children,
  valueClass = '',
}: {
  label: string;
  value: number | string;
  children?: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="bg-surface border border-border rounded-xl p-3.5 md:p-5 min-w-0">
      <div className="text-xs text-text3 font-medium mb-1">{label}</div>
      <div className={`text-2xl md:text-3xl font-bold leading-tight truncate ${valueClass}`}>
        {value}
      </div>
      <div className="text-xs text-text3 mt-1">{children}</div>
    </div>
  );
}

function ProgressBar({
  label,
  actual,
  goal,
  fmt,
}: {
  label: string;
  actual: number;
  goal: number;
  fmt?: (n: number) => string;
}) {
  const pct = goal > 0 ? Math.min(100, (actual / goal) * 100) : 0;
  const display = fmt ?? ((n: number) => String(n));
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="text-text3">
          {display(actual)} / {display(goal)} ({Math.round(pct)}%)
        </span>
      </div>
      <div className="h-3 bg-surface2 rounded overflow-hidden">
        <div
          className={pct >= 100 ? 'h-full bg-green' : 'h-full bg-accent'}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
